import React, { useContext, useEffect, useState } from "react";
import { ThemeContext } from "styled-components";
import parse from "html-react-parser";
import dompurify from "dompurify";
import {
  Alert,
  BankIcon,
  Box,
  ButtonWithAction,
  CardType,
  Checkbox,
  CloseIcon,
  Cluster,
  Detail,
  FormattedAddress,
  Stack,
  Text,
  Title,
  constants,
  util
} from "@thecb/components";
import AlertBar from "/components/alert-bar";
import RadioSection from "/components/radio-section";
import Slider, { SliderPosition } from "/components/slider";
import { SliderCloseButton } from "/components/slider/Slider";
import SliderPanel from "/components/slider-panel";
import FormattedCreditCard from "/components/formatted-credit-card";
import FormattedBankAccount from "/components/formatted-bank-account";
import IconsModule from "/components/obligation/modules/IconModule";
import TermsAndConditionsModal from "/components/terms-and-conditions-modal";
import { CREDIT_CARD_KIND } from "../../Profile.state";

const {
  WHITE,
  ATHENS_GREY,
  CHARADE_GREY,
  ERROR_COLOR,
  GHOST_GREY,
  STORM_GREY,
  ROYAL_BLUE_VIVID
} = constants.colors;
const { FONT_WEIGHT_SEMIBOLD } = constants.fontWeights;
const { displayCurrency, formatPercent } = util.general;
const { renderCardStatus } = util.formats;

const ConfirmAutopaySlider = ({
  isOpen,
  isMobile,
  onClose,
  onConfirm,
  paymentMethod,
  availablePaymentMethods = [],
  selectedAutopaymentMethodId,
  onPaymentMethodChange,
  address,
  detailedObligation,
  paymentConfigs,
  termsAgreed,
  onToggleTerms,
  addressJustAdded = false,
  paymentMethodJustAdded = false
}) => {
  const { metadata } = useContext(ThemeContext);
  const obligationSlug = detailedObligation?.config?.obligationSlug;
  const clientPaymentConfig =
    paymentConfigs?.[obligationSlug] ?? paymentConfigs.default;
  const CCFee = clientPaymentConfig.options.fees.creditCard;
  const hideFee = clientPaymentConfig.options?.hideFeeOnAutopay ?? false;
  const showProcessingFee =
    paymentMethod?.kind === CREDIT_CARD_KIND && !hideFee && CCFee.length > 0;
  const terms =
    paymentMethod?.kind === CREDIT_CARD_KIND
      ? parse(
          dompurify.sanitize(
            metadata?.data?.autopayTermsAndConditions?.creditCard?.extended ??
              ""
          )
        )
      : parse(
          dompurify.sanitize(
            metadata?.data?.autopayTermsAndConditions?.ach?.extended ?? ""
          )
        );

  const [termsError, setTermsError] = useState(false);
  const [successDismissed, setSuccessDismissed] = useState(false);
  const [paymentSuccessDismissed, setPaymentSuccessDismissed] = useState(false);
  const [showTermsPanel, setShowTermsPanel] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setTermsError(false);
      setSuccessDismissed(false);
      setPaymentSuccessDismissed(false);
      setShowTermsPanel(false);
    }
  }, [isOpen]);

  const paymentAlertText =
    paymentMethod?.kind === CREDIT_CARD_KIND
      ? `Card ending in ${paymentMethod.lastFour} has been added to your wallet`
      : `Account ending in ${paymentMethod?.lastFour} has been added to your wallet`;

  const firstObligation = detailedObligation?.obligations?.[0];
  const obligationName = detailedObligation?.name ?? "";
  const totalAmountDue =
    detailedObligation?.obligations?.reduce(
      (acc, curr) => acc + (curr.amountDue ?? 0),
      0
    ) ?? 0;

  const handleConfirm = () => {
    if (!termsAgreed) {
      setTermsError(true);
      return;
    }
    onConfirm();
  };

  return (
    <Slider
      isOpen={isOpen}
      isMobile={isMobile}
      position={SliderPosition.DOCUMENT_TOP}
      closeSlider={onClose}
      ariaLabelledBy="confirm-autopay-slider-title"
    >
      {!isMobile && (
        <SliderCloseButton
          closeAction={onClose}
          ariaLabel="Close Confirm Autopay Slider"
        />
      )}

      <SliderPanel status="onScreen" sliderKey="confirm-autopay-panel">
        <Box
          padding="0"
          minWidth="100%"
          minHeight="100%"
          extraStyles="display: flex; flex-direction: column; height: 100%;"
        >
          <Box
            background={CHARADE_GREY}
            padding="1.5rem"
            extraStyles={`
              position: sticky;
              top: 0;
              z-index: 10;
            `}
          >
            <Cluster
              justify="space-between"
              align="center"
              extraStyles="overflow: visible;"
            >
              <Title
                id="confirm-autopay-slider-title"
                color={WHITE}
                variant="small"
                as="h2"
              >
                Set Up Autopay
              </Title>
              {isMobile && (
                <ButtonWithAction
                  variant="smallGhost"
                  contentOverride
                  action={onClose}
                  dataQa="close-confirm-autopay-slider"
                  aria-label="Close"
                  extraStyles={`
                    padding: 0.15rem;
                    min-width: 1.5rem;
                    min-height: auto;
                    margin: 0;
                    svg { height: 12px; width: 12px; }
                  `}
                >
                  <CloseIcon
                    iconFill={WHITE}
                    iconWidth="12px"
                    iconHeight="12px"
                    aria-hidden="true"
                  />
                </ButtonWithAction>
              )}
            </Cluster>
          </Box>
          <Box
            background={WHITE}
            padding="0 1.5rem"
            extraStyles="overflow-y: auto; flex: 1; min-height: 0;"
          >
            <Box padding="1.5rem 0 0">
              <Stack childGap="1.5rem">
                <Stack childGap="0.5rem">
                  <Title variant="small" as="h3">
                    Confirm
                  </Title>
                  <Text variant="p">
                    By selecting this option you are enabling automatic
                    recurring payments.
                  </Text>
                </Stack>

                {paymentMethodJustAdded && !paymentSuccessDismissed && (
                  <AlertBar
                    alerts={[{ heading: paymentAlertText, variant: "success" }]}
                    deleteAlert={() => setPaymentSuccessDismissed(true)}
                  />
                )}

                {addressJustAdded && !successDismissed && (
                  <AlertBar
                    alerts={[
                      {
                        heading:
                          "Billing address has been saved to your account",
                        variant: "success"
                      }
                    ]}
                    deleteAlert={() => setSuccessDismissed(true)}
                  />
                )}

                {availablePaymentMethods.length > 1 && (
                  <Box padding="0">
                    <Detail
                      color={STORM_GREY}
                      weight={FONT_WEIGHT_SEMIBOLD}
                      as="h3"
                    >
                      Select a saved payment method
                    </Detail>
                    <Box padding="0.5rem 0 0">
                      <RadioSection
                        sections={availablePaymentMethods.map(method => ({
                          id: method.id,
                          titleIcon:
                            method.kind === CREDIT_CARD_KIND ? (
                              <CardType type={method.cardType} />
                            ) : (
                              <BankIcon />
                            ),
                          title:
                            method.kind === CREDIT_CARD_KIND
                              ? `Card ending in ${method.lastFour}`
                              : `${
                                  method.accountType === "CHECKING"
                                    ? "Checking"
                                    : "Savings"
                                } Account ending in ${method.lastFour}`,
                          rightTitleContent:
                            !!method.expirationStatus &&
                            renderCardStatus(
                              method.expirationStatus,
                              method.expireDate
                            )
                        }))}
                        toggleOpenSection={id => onPaymentMethodChange(id)}
                        openSection={
                          selectedAutopaymentMethodId
                            ? selectedAutopaymentMethodId
                            : false
                        }
                        staggeredAnimation
                        isMobile={isMobile}
                      />
                    </Box>
                  </Box>
                )}

                {availablePaymentMethods.length === 1 && paymentMethod && (
                  <Box
                    padding="1rem"
                    borderSize="1px"
                    borderColor={GHOST_GREY}
                    extraStyles="border-radius: 4px;"
                  >
                    {paymentMethod.kind === CREDIT_CARD_KIND ? (
                      <FormattedCreditCard
                        lastFour={paymentMethod.lastFour}
                        type={paymentMethod.cardType}
                        expireDate={paymentMethod.expireDate}
                        expirationStatus={paymentMethod.expirationStatus}
                      />
                    ) : (
                      <FormattedBankAccount
                        lastFour={paymentMethod.lastFour}
                        accountType={paymentMethod.accountType}
                      />
                    )}
                  </Box>
                )}

                {showProcessingFee && (
                  <Alert
                    heading="Processing Fee"
                    showQuitLink={false}
                    variant="info"
                    text={`There is a processing fee of ${
                      CCFee[0].type === "FLAT"
                        ? `${displayCurrency(CCFee[0].value)}`
                        : `${formatPercent(CCFee[0].value * 100)}%`
                    } ${
                      CCFee[0].minimumInCents
                        ? `with a minimum of ${displayCurrency(
                            CCFee[0].minimumInCents
                          )}`
                        : ""
                    } on all card payments.`}
                  />
                )}

                {address && (
                  <Box
                    padding="1rem"
                    borderSize="1px"
                    borderColor={GHOST_GREY}
                    extraStyles="border-radius: 4px;"
                  >
                    <FormattedAddress {...address} />
                  </Box>
                )}

                {firstObligation && (
                  <Box
                    padding="1rem"
                    borderSize="1px"
                    borderColor={GHOST_GREY}
                    extraStyles="border-radius: 4px;"
                  >
                    <Cluster
                      justify="space-between"
                      align="center"
                      childGap="4px"
                      nowrap
                    >
                      <Box padding="0">
                        <Cluster justify="flex-start" align="center">
                          {detailedObligation?.config?.iconDefault && (
                            <IconsModule
                              icon={detailedObligation.config.iconDefault}
                              iconColor={ROYAL_BLUE_VIVID}
                            />
                          )}
                          <Box padding="0">
                            <Stack childGap="0">
                              <Title
                                variant="small"
                                weight={FONT_WEIGHT_SEMIBOLD}
                                as="h3"
                              >
                                {obligationName}
                              </Title>
                              {firstObligation.subDescription && (
                                <Detail
                                  variant="small"
                                  color={STORM_GREY}
                                  as="p"
                                >
                                  {firstObligation.subDescription}
                                </Detail>
                              )}
                            </Stack>
                          </Box>
                        </Cluster>
                      </Box>
                      <Text variant="p" weight={FONT_WEIGHT_SEMIBOLD}>
                        {displayCurrency(totalAmountDue)}
                      </Text>
                    </Cluster>
                  </Box>
                )}

                <Box
                  padding="1.5rem"
                  minWidth="100%"
                  background={ATHENS_GREY}
                  borderRadius="4px"
                  extraStyles="overflow: visible;"
                >
                  <Stack childGap="0.25rem">
                    <Cluster
                      justify="flex-end"
                      align="center"
                      nowrap
                      extraStyles="overflow: visible;"
                    >
                      <Checkbox
                        name="terms-and-conditions"
                        error={termsError}
                        checked={termsAgreed}
                        onChange={() => {
                          if (termsError) setTermsError(false);
                          onToggleTerms();
                        }}
                        checkboxMargin="4px 8px 4px 4px"
                        extraStyles="align-self: flex-start;"
                        labelledById="terms-label"
                      />
                      <Cluster
                        justify="flex-start"
                        align="center"
                        nowrap
                        id="terms-label"
                        extraStyles="padding-right: 2px; > div > * { margin: 4px 2px; };"
                      >
                        <Text color={CHARADE_GREY}>I agree to the</Text>
                        <TermsAndConditionsModal
                          link="Terms and Conditions"
                          terms={terms}
                          isOpen={showTermsPanel}
                          toggleOpen={setShowTermsPanel}
                          linkVariant="default"
                          title="Terms and Conditions"
                          initialFocusSelector=".modal-close-button"
                        />
                      </Cluster>
                    </Cluster>
                    {termsError && (
                      <Cluster justify="flex-end">
                        <Text variant="pXS" color={ERROR_COLOR}>
                          Please accept Terms and Conditions
                        </Text>
                      </Cluster>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Box>
            <Box padding="1.5rem 0 1.5rem">
              <ButtonWithAction
                text="Confirm"
                variant="primary"
                action={handleConfirm}
                dataQa="Confirm"
                borderRadius="10px"
                extraStyles="width: 100%; margin: 0;"
              />
            </Box>
          </Box>
        </Box>
      </SliderPanel>
    </Slider>
  );
};

export default ConfirmAutopaySlider;
